import { GcpServiceAccountAccesses } from "@p0security/integrations-gcp-service-account-shared/accesses";
import { Access, Delegate } from "@p0security/shared/types/access/index";

import { GoogleDriveAiRoles } from "./permissions.ts";

export const GoogleDriveAiAccesses = {
  resource: Access({
    description: "Grant access to a folder or file in a Google shared drive",
    label: "Drive resource",
    maturity: "beta",
    delegation: {
      // The grantee is never the human. It is a short-lived service account
      // minted per grant, which this integration reaches by
      // delegation rather than by minting anything itself — nothing in
      // `gcp-service-account` names Drive, which is what lets another
      // Workspace target reuse it unchanged.
      "gcp-service-account": Delegate({
        resource: "gcp-service-account",
        accesses: GcpServiceAccountAccesses,
        key: "agentic-identity",
      }),
    },
    generated: {
      /** The gateway's instructions for acting as the grantee.
       *
       * Only here, not on the delegate: the gateway reads the block from a
       * delegate keyed `gcloud`, and otherwise from the root request, and this
       * integration's delegate is keyed `gcp-service-account`.
       */
      impersonate: {
        type: "object",
        properties: {
          audience: { type: "string" },
          provider: { type: "string" },
          serviceAccount: { type: "string" },
          subject: { type: "string" },
        },
      } as const,
    },
    parameters: {
      drive: {
        type: "static",
        label: "Shared drive",
        description: "The Google shared drive containing the target",
      },
      // Explicit rather than derived from the target, because deriving it would
      // mean asking a connector which drive a file is in before knowing which
      // connector to ask.
      resource: {
        type: "static",
        label: "Folder or file",
        description: "Google Drive ID of the folder or file to grant on",
        hint: "The ID in the item's Drive URL, e.g. /folders/<id>",
      },
      role: {
        type: "static",
        label: "Role",
        description: "Drive role to grant on the folder or file",
        choices: GoogleDriveAiRoles,
      },
      /** The grantee's service-account id, chosen at random when the request is
       * created and passed to the `gcp-service-account` delegate. Generated,
       * so no requestor can name an existing account. */
      accountId: {
        type: "generated",
        label: "Grantee service account id",
      } as const,
    },
    render: {
      locator: "drive",
      policy: "role",
      resource: "resource",
    } as const,
  }),
};

export type GoogleDriveAiAccesses = typeof GoogleDriveAiAccesses;
export type GoogleDriveAiResourceAccess = GoogleDriveAiAccesses["resource"];
